// PRD.184 / PRD.320 — Prescription Print settings: types, defaults, and the
// defensive `extra_json` resolver.
//
// Self-contained (no workspace-package imports) so it runs under the repo's
// node:test harness; `prescription-print-settings.ts` re-exports everything
// here and keeps the PowerSync QuerySpec.

// ─── Types ───

/** Catalog of system print templates available in v1 (PRD.184 §6.1). */
export type PrescriptionPrintTemplateId =
  | 'classic_clinical'
  | 'clean_classic'
  | 'compact_modern'
  | 'doctor_letterhead'
  | 'minimal_prescription'
  // PRD.284 — Letterhead Classic (traditional pre-printed stationery).
  | 'classic_letterhead'
  // PRD.320 — Dental Structured (clinical encounter document + Rx).
  | 'dental_structured';

/**
 * Which configured clinic logo to render on the printout. The actual image
 * URLs are env-provided on the client (VITE_ORG_LOGO_URL / VITE_ORG_LOGO_HORIZONTAL_URL),
 * so the saved setting only stores the choice.
 */
export type PrescriptionPrintLogoSource = 'standard' | 'horizontal' | 'none';

/** PowerSync sqlite row shape — booleans are stored as 0/1 integers. */
export interface PrescriptionPrintSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  template_id: string | null;
  logo_source: string | null;
  clinic_name: string | null;
  clinic_address: string | null;
  clinic_phone: string | null;
  clinic_email: string | null;
  clinic_website: string | null;
  footer_text: string | null;
  show_patient_demographics: number | null;
  show_complaint: number | null;
  show_vitals: number | null;
  show_item_details: number | null;
  show_instructions: number | null;
  show_doctor_footer: number | null;
  show_signature_block: number | null;
  /** PRD.320 — JSON string of extensible presentation settings. */
  extra_json: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** UI-friendly settings shape used by the wizard. */
export interface PrescriptionPrintSettingsUI {
  id: string;
  templateId: PrescriptionPrintTemplateId;

  logoSource: PrescriptionPrintLogoSource;
  clinicName: string;
  clinicAddress: string;
  clinicPhone: string;
  clinicEmail: string;
  clinicWebsite: string;
  footerText: string;

  showPatientDemographics: boolean;
  showComplaint: boolean;
  showVitals: boolean;
  showItemDetails: boolean;
  showInstructions: boolean;
  showDoctorFooter: boolean;
  showSignatureBlock: boolean;

  /**
   * PRD.320 — Dental Structured clinical sections. Chief Complaint and Vitals
   * are NOT here: they keep their existing dedicated columns
   * (`show_complaint` / `show_vitals`), which stay authoritative.
   */
  sections: PrescriptionPrintSectionSettings;
  /** PRD.320 — logo/header sizing and vitals presentation. */
  layout: PrescriptionPrintLayoutSettings;
  /** PRD.320 — overall + per-section type scale. */
  typography: PrescriptionPrintTypographySettings;
  /** PRD.320 — `lines` vs `comma` per list-backed clinical section. */
  clinicalSectionLayout: PrescriptionPrintClinicalLayoutSettings;
  /**
   * Anything else already in `extra_json`. Round-tripped untouched so a newer
   * client's settings are never destroyed by an older one saving over them.
   */
  extra: Record<string, unknown>;
}

/** PRD.320 — per-section visibility for the Dental Structured clinical block. */
export interface PrescriptionPrintSectionSettings {
  /**
   * MASTER toggle for the whole Clinical Note area. When off, the header and
   * every subsection below are suppressed and no vertical space is left behind
   * — subsection settings are simply not consulted.
   */
  clinicalNotes: boolean;
  clinicalFindings: boolean;
  investigations: boolean;
  diagnosis: boolean;
  treatmentAdvised: boolean;
  treatmentDone: boolean;
}

// ─── Presentation enums ───
//
// Semantic PRESETS, never raw CSS. The wizard must not become a free-form
// stylesheet editor, and a preset can be re-tuned centrally when the A4 layout
// changes without touching a single clinic's saved settings.

export const LOGO_SIZES = ['small', 'medium', 'large'] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];

export const HEADER_SIZES = ['compact', 'standard', 'large'] as const;
export type HeaderSize = (typeof HEADER_SIZES)[number];

export const VITALS_LAYOUTS = ['table', 'inline', 'strip'] as const;
export type VitalsLayout = (typeof VITALS_LAYOUTS)[number];

export const FONT_SCALES = ['small', 'standard', 'large'] as const;
export type FontScale = (typeof FONT_SCALES)[number];

/** Per-section typography: an explicit scale, or defer to the overall setting. */
export const FONT_SCALE_CHOICES = ['inherit', ...FONT_SCALES] as const;
export type FontScaleChoice = (typeof FONT_SCALE_CHOICES)[number];

/** How a list-backed clinical section renders its entries. */
export const MULTI_VALUE_LAYOUTS = ['lines', 'comma'] as const;
export type MultiValueLayout = (typeof MULTI_VALUE_LAYOUTS)[number];

/** Relative type scale per preset. Applied to the template's base sizes. */
export const FONT_SCALE_FACTORS: Record<FontScale, number> = {
  small: 0.9,
  standard: 1,
  large: 1.12,
};

export interface PrescriptionPrintLayoutSettings {
  logoSize: LogoSize;
  /** Header scale/spacing — deliberately INDEPENDENT of `logoSize`. */
  headerSize: HeaderSize;
  vitalsLayout: VitalsLayout;
}

export interface PrescriptionPrintTypographySettings {
  overall: FontScale;
  header: FontScaleChoice;
  patientDetails: FontScaleChoice;
  vitals: FontScaleChoice;
  clinicalNote: FontScaleChoice;
  prescription: FontScaleChoice;
  footer: FontScaleChoice;
}

/** Per-section list rendering for the five list-backed clinical sections. */
export interface PrescriptionPrintClinicalLayoutSettings {
  clinicalFindings: MultiValueLayout;
  investigations: MultiValueLayout;
  diagnosis: MultiValueLayout;
  treatmentAdvised: MultiValueLayout;
  treatmentDone: MultiValueLayout;
}

/**
 * All ON by default.
 *
 * Choosing Dental Structured must immediately produce the clinical document the
 * template exists for; defaulting these off would make a freshly-selected
 * template look broken. Empty sections still collapse, so "on" costs nothing
 * when there is no data.
 */
export const PRESCRIPTION_PRINT_SECTION_DEFAULTS: PrescriptionPrintSectionSettings = {
  clinicalNotes: true,
  clinicalFindings: true,
  investigations: true,
  diagnosis: true,
  treatmentAdvised: true,
  treatmentDone: true,
};

export const PRESCRIPTION_PRINT_LAYOUT_DEFAULTS: PrescriptionPrintLayoutSettings = {
  logoSize: 'medium',
  headerSize: 'standard',
  vitalsLayout: 'table',
};

export const PRESCRIPTION_PRINT_TYPOGRAPHY_DEFAULTS: PrescriptionPrintTypographySettings = {
  overall: 'standard',
  header: 'inherit',
  patientDetails: 'inherit',
  vitals: 'inherit',
  clinicalNote: 'inherit',
  prescription: 'inherit',
  footer: 'inherit',
};

export const PRESCRIPTION_PRINT_CLINICAL_LAYOUT_DEFAULTS: PrescriptionPrintClinicalLayoutSettings =
  {
    clinicalFindings: 'lines',
    investigations: 'lines',
    diagnosis: 'lines',
    treatmentAdvised: 'lines',
    treatmentDone: 'lines',
  };

export const PRESCRIPTION_PRINT_DEFAULTS: Omit<PrescriptionPrintSettingsUI, 'id'> = {
  templateId: 'classic_clinical',
  logoSource: 'standard',
  clinicName: '',
  clinicAddress: '',
  clinicPhone: '',
  clinicEmail: '',
  clinicWebsite: '',
  footerText: '',
  showPatientDemographics: true,
  showComplaint: false,
  showVitals: false,
  showItemDetails: true,
  showInstructions: true,
  showDoctorFooter: true,
  showSignatureBlock: true,
  sections: PRESCRIPTION_PRINT_SECTION_DEFAULTS,
  layout: PRESCRIPTION_PRINT_LAYOUT_DEFAULTS,
  typography: PRESCRIPTION_PRINT_TYPOGRAPHY_DEFAULTS,
  clinicalSectionLayout: PRESCRIPTION_PRINT_CLINICAL_LAYOUT_DEFAULTS,
  extra: {},
};

// ─── Mappers ───

function asBool(v: number | null, fallback: boolean): boolean {
  if (v == null) return fallback;
  return v === 1;
}

/**
 * Parse `extra_json` defensively.
 *
 * NULL, `{}`, malformed JSON, a JSON array, or a non-object all degrade to
 * "nothing configured" rather than throwing — a settings blob must never be
 * able to break a print path.
 */
function parseExtraJson(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Read section visibility from a parsed `extra_json`.
 *
 * Every key falls back INDIVIDUALLY, so a partially-written blob (an older
 * client that knew only three of the five) still yields sane values for the
 * rest rather than discarding the whole object.
 *
 * Only real booleans are honoured. A string `"false"` or a number is treated as
 * unset, because coercing it would silently hide a clinical section.
 */
/** Read an enum-like value, falling back when it is absent or not a member. */
function readEnum<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return typeof raw === 'string' && (allowed as readonly string[]).includes(raw)
    ? (raw as T)
    : fallback;
}

/** Narrow an `extra_json` sub-object, tolerating arrays and non-objects. */
function readGroup(extra: Record<string, unknown>, key: string): Record<string, unknown> {
  const raw = extra[key];
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/**
 * Logo/header sizing and vitals presentation.
 *
 * Each key falls back INDIVIDUALLY and an unrecognised enum value (a typo, or a
 * preset a newer client wrote) degrades to the default rather than reaching the
 * renderer — a bad settings blob must never produce a broken printout.
 */
export function readPrescriptionPrintLayout(
  extra: Record<string, unknown>,
): PrescriptionPrintLayoutSettings {
  const o = readGroup(extra, 'layout');
  const d = PRESCRIPTION_PRINT_LAYOUT_DEFAULTS;
  return {
    logoSize: readEnum(o.logoSize, LOGO_SIZES, d.logoSize),
    headerSize: readEnum(o.headerSize, HEADER_SIZES, d.headerSize),
    vitalsLayout: readEnum(o.vitalsLayout, VITALS_LAYOUTS, d.vitalsLayout),
  };
}

/** Overall + per-section type scale. Per-section defaults to `inherit`. */
export function readPrescriptionPrintTypography(
  extra: Record<string, unknown>,
): PrescriptionPrintTypographySettings {
  const o = readGroup(extra, 'typography');
  const d = PRESCRIPTION_PRINT_TYPOGRAPHY_DEFAULTS;
  const section = (key: keyof PrescriptionPrintTypographySettings) =>
    readEnum(o[key], FONT_SCALE_CHOICES, d[key] as FontScaleChoice);
  return {
    overall: readEnum(o.overall, FONT_SCALES, d.overall),
    header: section('header'),
    patientDetails: section('patientDetails'),
    vitals: section('vitals'),
    clinicalNote: section('clinicalNote'),
    prescription: section('prescription'),
    footer: section('footer'),
  };
}

/** `lines` vs `comma` per list-backed clinical section. */
export function readPrescriptionPrintClinicalLayout(
  extra: Record<string, unknown>,
): PrescriptionPrintClinicalLayoutSettings {
  const o = readGroup(extra, 'clinicalSectionLayout');
  const d = PRESCRIPTION_PRINT_CLINICAL_LAYOUT_DEFAULTS;
  const pick = (key: keyof PrescriptionPrintClinicalLayoutSettings) =>
    readEnum(o[key], MULTI_VALUE_LAYOUTS, d[key]);
  return {
    clinicalFindings: pick('clinicalFindings'),
    investigations: pick('investigations'),
    diagnosis: pick('diagnosis'),
    treatmentAdvised: pick('treatmentAdvised'),
    treatmentDone: pick('treatmentDone'),
  };
}

/**
 * Resolve the effective type scale for one section.
 *
 * `inherit` means "follow the overall setting" — the per-section choice
 * overrides it only for that section, never the other way round.
 */
export function resolveFontScale(
  typography: PrescriptionPrintTypographySettings,
  section: Exclude<keyof PrescriptionPrintTypographySettings, 'overall'>,
): FontScale {
  const choice = typography[section];
  return choice === 'inherit' ? typography.overall : choice;
}

/** Multiplier for a section's type, relative to the template's base sizes. */
export function fontScaleFactor(
  typography: PrescriptionPrintTypographySettings,
  section: Exclude<keyof PrescriptionPrintTypographySettings, 'overall'>,
): number {
  return FONT_SCALE_FACTORS[resolveFontScale(typography, section)];
}

export function readPrescriptionPrintSections(
  extra: Record<string, unknown>,
): PrescriptionPrintSectionSettings {
  const raw = extra.sections;
  const o =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const pick = (key: keyof PrescriptionPrintSectionSettings) =>
    typeof o[key] === 'boolean' ? (o[key] as boolean) : PRESCRIPTION_PRINT_SECTION_DEFAULTS[key];

  return {
    clinicalNotes: pick('clinicalNotes'),
    clinicalFindings: pick('clinicalFindings'),
    investigations: pick('investigations'),
    diagnosis: pick('diagnosis'),
    treatmentAdvised: pick('treatmentAdvised'),
    treatmentDone: pick('treatmentDone'),
  };
}

export function mapPrescriptionPrintSettingsToUI(
  row: PrescriptionPrintSettingsRow,
): PrescriptionPrintSettingsUI {
  const d = PRESCRIPTION_PRINT_DEFAULTS;
  const extra = parseExtraJson(row.extra_json);
  return {
    id: row.id,
    templateId: (row.template_id ?? d.templateId) as PrescriptionPrintTemplateId,

    logoSource: (row.logo_source ?? d.logoSource) as PrescriptionPrintLogoSource,
    clinicName: row.clinic_name ?? '',
    clinicAddress: row.clinic_address ?? '',
    clinicPhone: row.clinic_phone ?? '',
    clinicEmail: row.clinic_email ?? '',
    clinicWebsite: row.clinic_website ?? '',
    footerText: row.footer_text ?? '',

    showPatientDemographics: asBool(row.show_patient_demographics, d.showPatientDemographics),
    showComplaint: asBool(row.show_complaint, d.showComplaint),
    showVitals: asBool(row.show_vitals, d.showVitals),
    showItemDetails: asBool(row.show_item_details, d.showItemDetails),
    showInstructions: asBool(row.show_instructions, d.showInstructions),
    showDoctorFooter: asBool(row.show_doctor_footer, d.showDoctorFooter),
    showSignatureBlock: asBool(row.show_signature_block, d.showSignatureBlock),

    sections: readPrescriptionPrintSections(extra),
    layout: readPrescriptionPrintLayout(extra),
    typography: readPrescriptionPrintTypography(extra),
    clinicalSectionLayout: readPrescriptionPrintClinicalLayout(extra),
    // Everything else survives untouched, including `sections` itself — it is
    // re-serialized from the resolved values on save.
    extra,
  };
}

export function mapUIToPrescriptionPrintSettingsColumns(
  ui: Omit<PrescriptionPrintSettingsUI, 'id'>,
) {
  const b = (v: boolean) => (v ? 1 : 0);
  return {
    template_id: ui.templateId,
    logo_source: ui.logoSource,
    clinic_name: ui.clinicName || null,
    clinic_address: ui.clinicAddress || null,
    clinic_phone: ui.clinicPhone || null,
    clinic_email: ui.clinicEmail || null,
    clinic_website: ui.clinicWebsite || null,
    footer_text: ui.footerText || null,

    show_patient_demographics: b(ui.showPatientDemographics),
    show_complaint: b(ui.showComplaint),
    show_vitals: b(ui.showVitals),
    show_item_details: b(ui.showItemDetails),
    show_instructions: b(ui.showInstructions),
    show_doctor_footer: b(ui.showDoctorFooter),
    show_signature_block: b(ui.showSignatureBlock),

    // PRD.320 — unknown keys are spread back FIRST so a newer client's settings
    // survive an older one saving over them; only `sections` is overwritten
    // with the values this build actually resolved.
    extra_json: JSON.stringify({
      ...(ui.extra ?? {}),
      sections: ui.sections,
      layout: ui.layout,
      typography: ui.typography,
      clinicalSectionLayout: ui.clinicalSectionLayout,
    }),

    updated_at: new Date().toISOString(),
  };
}
